import { useEffect, useRef } from 'react';
import { useApolloClient } from '@apollo/client/react';
import { useSmallAppEditorStore } from '@/stores/smallapp-editor-store';
import { getFolderPathFromCache, getInMemoryCache } from '@/lib/cache-utils';

/**
 * #1724 — Keep an OPEN SmallApp editor's destination folder path in sync when
 * another session renames that folder.
 *
 * The realtime layer broadcasts every subscription event as a `realtime:event`
 * window CustomEvent (see useRealtimeSubscription.ts). On a GROUP UPDATE whose
 * `_id` matches the folder a create-mode editor is targeting, we read the
 * folder's fresh canonical path from the normalized cache and remap the editor's
 * `currentPath` — the notify-then-fetch pattern asset rows already use, applied
 * to the open editor's destination folder.
 *
 * Timing: `useAssetSubscriptions` refetches `AssetGroups` on the same event but
 * on a 500ms debounce, so the normalized Group entity isn't fresh the instant
 * the event fires. We read after a short settle delay so we pick up the renamed
 * path, not the stale one.
 *
 * This is defense-in-depth / live-UX only. Correctness is guaranteed by the
 * save-time re-resolve in useSmallAppSave / useSmallAppMicroService (which reads
 * the freshest cache at the moment of save) and by the authoritative backend
 * path canonicalization in BaseService.saveItem. Mounted once in AppShell.
 */
const SETTLE_MS = 650; // > the 500ms AssetGroups refetch debounce

export function useEditorFolderPathSync(): void {
  const client = useApolloClient();
  const timersRef = useRef<Set<ReturnType<typeof setTimeout>>>(new Set());

  useEffect(() => {
    const timers = timersRef.current;

    const handle = (e: Event) => {
      const detail = (e as CustomEvent).detail as
        | { type?: string; _id?: string; mutation?: string }
        | undefined;
      if (!detail || detail.type?.toLowerCase() !== 'group' || detail.mutation !== 'UPDATE') return;
      const groupId = detail._id;
      if (!groupId) return;

      // Only react if an open create-mode editor targets this exact folder.
      const targeted = useSmallAppEditorStore
        .getState()
        .stack.some((entry) => entry.options.currentGroup?._id === groupId && !entry.asset?._id);
      if (!targeted) return;

      // Read AFTER the debounced AssetGroups refetch lands so the cache is fresh.
      const timer = setTimeout(() => {
        timers.delete(timer);
        const freshPath = getFolderPathFromCache(getInMemoryCache(client), groupId);
        if (freshPath) {
          useSmallAppEditorStore.getState().syncEditorFolderPath(groupId, freshPath);
        }
      }, SETTLE_MS);
      timers.add(timer);
    };

    window.addEventListener('realtime:event', handle);
    return () => {
      window.removeEventListener('realtime:event', handle);
      timers.forEach(clearTimeout);
      timers.clear();
    };
  }, [client]);
}
